import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Iphone } from '../magicui/iphone';
import SimuladorWhatsApp from './SimuladorWhatsApp';

/**
 * El teléfono del hero: cuenta el producto en dos tiempos.
 *
 *   1. Un cliente pide un turno por WhatsApp y el bot se lo da.
 *   2. Ese mismo turno aparece cargado en la agenda del negocio.
 *
 *
 * POR QUÉ UN SOLO APARATO Y NO DOS
 *
 * Antes el hero tenía el teléfono al lado de una maqueta de navegador, y casi
 * todos los problemas de composición venían de ahí: el teléfono tapaba el
 * contenido del navegador, el navegador empujaba al teléfono fuera de cuadro, y
 * agrandar cualquiera de los dos rompía al otro. Dos aparatos peleando el mismo
 * espacio no se arregla moviéndolos.
 *
 * Con uno solo el problema desaparece, y además la secuencia cuenta algo que dos
 * maquetas quietas no pueden contar: la CONSECUENCIA. Que el bot conteste es la
 * mitad; que el turno quede cargado sin que nadie lo anote es el producto.
 *
 *
 * LA AGENDA ESTÁ DIBUJADA, NO CAPTURADA, y eso va contra el criterio del resto
 * de la landing —producto real, no ilustración—, así que conviene decir por qué:
 * se intentó capturar el panel a 406px de ancho y no sirve. En esa medida los
 * controles se comen la pantalla antes de que aparezca un turno, el estado de la
 * demo dice "No hay turnos", y encima flota el panel de "Pruebas de Trial", que
 * es una herramienta de desarrollo con botones que dicen FORZAR BLOQUEO.
 *
 * Lo que hace aceptable el dibujo es que muestra exactamente lo que el producto
 * hace: la hora, el servicio y la profesional son los mismos que el bot confirma
 * en el chat de al lado. Si el panel móvil mejora, esto se reemplaza por una
 * captura y el componente no cambia.
 *
 *
 * NINGUNA FASE TIENE ESTADO VACÍO, que es la condición para que el bucle no
 * moleste. El chat entra escribiéndose —eso ES el argumento, no una carencia— y
 * la agenda entra ya completa. Durante el deslizamiento las dos pantallas están
 * montadas, así que no hay ningún cuadro en blanco. Es la lección de la versión
 * anterior, donde el simulador borraba la pantalla en cada vuelta y el teléfono
 * quedaba beige una cuarta parte del tiempo.
 */

/* El guion. Las dos trampas son deliberadas y son lo único que esta pieza tiene
   que demostrar: un bot de menú de botones muere en las dos.
     - "con la misma chica de la otra vez" → memoria de con quién se atendió.
     - "el de las 4 y media" → nadie escribe 16:30.

   Constante de módulo y no literal en el JSX: `SimuladorWhatsApp` lo toma como
   dependencia de su efecto, y un array nuevo en cada render reinicia el bucle. */
const CONVERSACION = [
  { de: 'cliente', texto: 'hola! tenés algo el jueves a la tarde?\ncon la misma chica de la otra vez' },
  { de: 'bot', texto: 'Con Carla el jueves queda 15:00, 16:30 y 18:00.\n¿Cuál te viene bien?' },
  { de: 'cliente', texto: 'el de las 4 y media' },
  { de: 'bot', texto: 'Listo ✅\nJueves 16:30 · Corte + color · con Carla' },
];

/* La agenda. El turno de 16:30 es EL MISMO que el bot confirma arriba: misma
   hora, mismo servicio, misma profesional. Si se toca uno hay que tocar el otro,
   porque la continuidad entre las dos pantallas es todo el argumento. */
const TURNOS = [
  { hora: '15:00', quien: 'Martín L.', servicio: 'Corte', persona: 'Carla' },
  { hora: '16:30', quien: 'Sofía G.', servicio: 'Corte + color', persona: 'Carla', nuevo: true },
  { hora: '18:00', quien: null },
];

/* Cuánto dura cada tramo. El chat necesita ~11s para escribirse entero y quedar
   un momento completo antes de irse; la agenda se lee en 5. */
const MS_CHAT = 9000;
const MS_AGENDA = 5500;

/* Cuánto espera el turno nuevo antes de caer, contado desde que arranca el
   deslizamiento. El deslizamiento dura 600ms, así que 950 deja ver la agenda
   como estaba —dos turnos— antes de que aparezca el tercero. Cayendo durante
   el deslizamiento se pierde: la pantalla todavía está entrando. */
const MS_HASTA_QUE_CAE = 950;

/* Lo que tarda la tira en correrse de una pantalla a la otra. Está acá y no
   suelto en el JSX porque ahora lo necesitan dos cosas: la animación misma y
   el momento en que se puede quitar el turno sin que se vea desaparecer. */
const MS_DESLIZAMIENTO = 600;

function PantallaDeWhatsApp({ ciclo }) {
  return (
    <div className="flex h-full flex-col bg-[#ECE5DD]">
      <div className="flex items-center gap-2 bg-[#075E54] px-3 pb-2.5 pt-4 text-white">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/25 text-[0.95rem] font-semibold">
          EN
        </span>
        <span className="min-w-0 flex-1 truncate text-[1.05rem] font-semibold leading-tight">
          Estudio Norte
          <span className="block text-[0.85rem] font-normal text-white/70">en línea</span>
        </span>
      </div>

      <div className="flex justify-center pt-2">
        <span className="rounded bg-white/70 px-2.5 py-1 text-[0.7rem] font-medium uppercase tracking-wide text-neutral-500">
          Hoy
        </span>
      </div>

      {/* `key={ciclo}` remonta el simulador en cada vuelta, que es lo que hace
          que la conversación se vuelva a escribir. Con `repetir={false}` corre
          una sola vez y queda completa, así que sin el remonte quedaría quieta
          para siempre después del primer pase.

          Las clases de adentro se pisan acá y no en el componente porque el
          mismo simulador lo usan otros bloques donde sus valores están bien:
          apila los mensajes contra abajo y reserva 260px de alto, y en esta
          pantalla tienen que crecer desde arriba. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden [&>div>div]:!min-h-0 [&>div>div]:!justify-start [&>div>div]:!gap-2 [&>div>div]:!rounded-none [&>div>div]:!bg-transparent [&>div>div]:!p-3 [&_p:nth-child(odd)]:!bg-[#DCF8C6] [&_p:nth-child(odd)]:!text-neutral-800 [&_p]:!px-3.5 [&_p]:!py-2.5 [&_p]:!text-[1rem] [&_p]:!leading-[1.45]">
        <SimuladorWhatsApp key={ciclo} conversacion={CONVERSACION} repetir={false} />
      </div>
    </div>
  );
}

/**
 * La agenda del negocio.
 *
 * EL TURNO TIENE QUE VERSE LLEGAR, no estar. Antes esta pantalla entraba con
 * los tres turnos ya puestos y el nuevo marcado con una etiqueta: se leía como
 * una lista cualquiera: había que buscar cuál era el nuevo, y para cuando lo
 * encontrabas ya se había ido a la fase siguiente. El argumento de la fila no
 * es que el negocio tenga una agenda —eso lo tiene cualquiera— sino que el
 * turno se carga SOLO. Eso es un cambio, y un cambio no se muestra con una
 * foto.
 *
 * Así que entra con los dos turnos que ya estaban y el de las 16:30 aparece
 * después, junto con el aviso de arriba. El encabezado dice "Gestión de
 * turnos" porque es literalmente lo que dice el panel de verdad.
 *
 * @param llego  Si el turno nuevo ya cayó. Lo decide el ciclo de afuera.
 */
function PantallaDeAgenda({ llego }) {
  const visibles = llego ? TURNOS : TURNOS.filter((t) => !t.nuevo);

  return (
    <div className="flex h-full flex-col bg-[#F6F6F7]">
      <div className="bg-white px-4 pb-3 pt-5">
        <p className="text-[0.75rem] font-semibold uppercase tracking-[0.14em] text-neutral-400">
          Gestión de turnos
        </p>
        <div className="mt-1 flex items-center justify-between gap-2">
          <p className="font-titular text-[1.6rem] font-semibold leading-tight text-neutral-900">
            Jueves 14
          </p>
          {/* El aviso aparece con el turno, no antes: es la mitad del gesto.
              `AnimatePresence` y no un `hidden`, para que también se vaya
              cuando el ciclo vuelve al chat. */}
          <AnimatePresence>
            {llego && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="shrink-0 rounded-full bg-[#FF5722] px-2.5 py-1 text-[0.7rem] font-bold text-white"
              >
                1 turno nuevo
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ul className="flex flex-col gap-2 p-3">
        {visibles.map((t) => (
          <motion.li
            key={t.hora}
            /* Sólo el que llega se anima. Los que ya estaban se dibujan
               quietos: si entraran todos, volvería a no entenderse cuál es la
               novedad. `height` va en la animación porque el que entra tiene
               que EMPUJAR a los de abajo — apareciendo en un hueco ya
               reservado, no se lee como que llegó. */
            initial={t.nuevo ? { opacity: 0, height: 0, marginBottom: -8 } : false}
            animate={t.nuevo ? { opacity: 1, height: "auto", marginBottom: 0 } : false}
            transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
            className={
              t.quien
                ? `flex gap-3 rounded-xl bg-white p-3 ${t.nuevo ? 'ring-2 ring-[#FF5722]' : 'ring-1 ring-neutral-200'}`
                : 'flex gap-3 rounded-xl border border-dashed border-neutral-300 p-3'
            }
          >
            <span
              className={`w-[3.8rem] shrink-0 text-[1rem] font-semibold tabular-nums ${
                t.quien ? 'text-neutral-900' : 'text-neutral-400'
              }`}
            >
              {t.hora}
            </span>

            {t.quien ? (
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-[1rem] font-semibold text-neutral-900">
                    {t.quien}
                  </span>
                  {/* El único acento de color de la pantalla: marca cuál de los
                      tres turnos acaba de entrar, que es lo que conecta esta
                      pantalla con el chat anterior. */}
                  {t.nuevo && (
                    <span className="shrink-0 rounded-full bg-[#FF5722] px-2 py-[2px] text-[0.65rem] font-bold uppercase tracking-wide text-white">
                      Nuevo
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-[0.9rem] text-neutral-500">
                  {t.servicio} · {t.persona}
                </span>
              </span>
            ) : (
              <span className="flex-1 text-[0.95rem] text-neutral-400">Libre</span>
            )}
          </motion.li>
        ))}
      </ul>

      <p className="mt-auto px-4 pb-5 text-[0.8rem] leading-snug text-neutral-400">
        Entró por WhatsApp. Nadie lo anotó.
      </p>
    </div>
  );
}

/**
 * @param className  Para el tamaño y la posición; el componente no decide eso.
 */
export default function TelefonoDelHero({ className = '' }) {
  const [enAgenda, setEnAgenda] = useState(false);
  const [ciclo, setCiclo] = useState(0);
  const [llego, setLlego] = useState(false);

  /* El turno cae después de que la agenda terminó de entrar, y se retira al
     volver al chat para que la vuelta siguiente lo muestre llegando otra vez.
     Es un efecto aparte del que maneja las fases: mezclarlos obliga a anidar
     dos temporizadores y el de adentro sobrevive al cambio de fase. */
  useEffect(() => {
    /* AL VOLVER AL CHAT, EL TURNO SE QUITA RECIÉN CUANDO LA AGENDA YA NO SE VE.

       Quitándolo apenas cambia la fase, se lo ve desaparecer durante los 600ms
       que dura el deslizamiento — y un turno que se borra solo delante tuyo
       dice exactamente lo contrario de lo que la fila quiere decir. Esperando a
       que la pantalla salga de cuadro, el reinicio ocurre a escondidas y la
       vuelta siguiente lo muestra llegando de nuevo. */
    const id = enAgenda
      ? setTimeout(() => setLlego(true), MS_HASTA_QUE_CAE)
      : setTimeout(() => setLlego(false), MS_DESLIZAMIENTO + 120);
    return () => clearTimeout(id);
  }, [enAgenda]);

  useEffect(() => {
    const id = setTimeout(
      () => {
        if (enAgenda) {
          // Volver al chat: sube el ciclo, y eso remonta el simulador.
          setCiclo((n) => n + 1);
          setEnAgenda(false);
        } else {
          setEnAgenda(true);
        }
      },
      enAgenda ? MS_AGENDA : MS_CHAT
    );
    return () => clearTimeout(id);
  }, [enAgenda]);

  /* EL ALTO DECIDE LA PROPORCIÓN, y bajó de 882 a 470 en dos pasos por dos
     motivos distintos.

     882 sobre 433 de ancho es la proporción de un iPhone real: 1 a 2, bien
     alargada. Yendo solo en el hero ocupaba casi toda la ventana de alto, así
     que se bajó a 600 (≈3:4).

     Ahora bajó otra vez, a 470, porque el teléfono pasó a llenar su columna:
     de ~350px de ancho a ~517. A 600 eso daba 716px de alto para un chat de
     cuatro globos y una agenda de tres turnos — más de la mitad de la pantalla
     quedaba vacía, y un aparato grande medio vacío se ve peor que uno chico
     lleno. A 470 la proporción es casi cuadrada y el contenido llega abajo.

     Los tamaños de letra de adentro también subieron ~25% en la misma tanda:
     estaban calibrados para el teléfono chico y son valores fijos en rem, así
     que al agrandar el aparato se veían proporcionalmente más chicos. */
  return (
    <Iphone alto={470} className={className}>
      {/* Las dos pantallas montadas a la vez, una al lado de la otra, y lo que
          se mueve es la tira. Así el deslizamiento se lee como cambiar de app
          en un teléfono de verdad, y nunca hay un cuadro sin nada.

          FRAMER Y NO `transition-transform`, aunque sea una sola propiedad en
          un solo elemento. `scripts/check-ui.js` falla cualquier elemento que
          declare `transform` en su `transition-property`, y la regla no
          distingue el caso legítimo del que la motivó —un selector `*` en
          `WhatsAppAssistant.css` que le puso una transición a TODO y dejó
          todos los resortes de framer hechos puré—. Un guardarraíl que sólo
          sirve si nadie lo desactiva "por esta vez"; framer escribe el
          transform cuadro por cuadro y no declara ninguna transición, así que
          hace lo mismo sin encender la alarma. */}
      <motion.div
        className="flex h-full w-[200%]"
        animate={{ x: enAgenda ? '-50%' : '0%' }}
        transition={{ duration: MS_DESLIZAMIENTO / 1000, ease: [0.65, 0, 0.35, 1] }}
      >
        <div className="h-full w-1/2 shrink-0">
          <PantallaDeWhatsApp ciclo={ciclo} />
        </div>
        <div className="h-full w-1/2 shrink-0">
          <PantallaDeAgenda llego={llego} />
        </div>
      </motion.div>
    </Iphone>
  );
}
